import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, CircularProgress, Fade, IconButton, Paper, Snackbar, Tooltip, Typography } from '@mui/material';
import { alpha, keyframes, useTheme } from '@mui/material/styles';
import { SYNC_OPERATION_META, type SyncOperationKind } from 'pages/RecognitionPages/Recognitions/utils';
import { type FC, useState } from 'react';

const countdown = keyframes`
  from { width: 100%; }
  to   { width: 0%; }
`;

interface SyncActiveToastProps {
  open: boolean;
  personName: string;
  progress?: number;
  statusMessage?: string;
  success?: boolean;
  /** When success=true, bar counts down and calls onClose after this duration. 0 = no auto-close. */
  successLingerMs?: number;
  operation?: SyncOperationKind;
  /** Sobrescreve a mensagem padrão de "concluído" do operation (ex: contagem de sucesso/falha real). */
  doneMessageOverride?: string;
  onView: () => void;
  onClose: () => void;
}

const OPERATION_PALETTE_KEY: Record<SyncOperationKind, 'primary' | 'info' | 'error'> = {
  create: 'primary',
  update: 'info',
  delete: 'error',
};

const SyncActiveToast: FC<SyncActiveToastProps> = ({
  open,
  personName,
  progress,
  statusMessage,
  success = false,
  successLingerMs = 0,
  operation = 'create',
  doneMessageOverride,
  onView,
  onClose,
}) => {
  const theme   = useTheme();
  const pct     = Math.max(0, Math.min(100, progress ?? 0));
  const accent  = success ? theme.palette.success.main : theme.palette[OPERATION_PALETTE_KEY[operation]].main;
  const [hovered, setHovered] = useState(false);

  const secondaryLine = success
    ? (doneMessageOverride ?? SYNC_OPERATION_META[operation].doneMessage)
    : statusMessage || 'Aguardando...';

  const showCountdown = success && successLingerMs > 0;

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      TransitionComponent={Fade}
      transitionDuration={{ enter: 200, exit: 350 }}
      sx={{ zIndex: 99999999 }}
    >
      <Paper
        elevation={0}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        sx={{
          minWidth: 300,
          maxWidth: 380,
          backgroundColor: 'var(--app-surface-2)',
          border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
          borderRadius: 1.5,
          overflow: 'hidden',
          position: 'relative',
          boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.55)}`,
        }}
      >
        <Box sx={{ px: 1.75, pt: 1.4, pb: (progress !== undefined || showCountdown) ? 1.75 : 1.4 }}>

          {/* Row 1 — icon + name + close */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            {success
              ? <CheckCircleIcon sx={{ fontSize: 18, color: accent, flexShrink: 0 }} />
              : <CircularProgress size={16} thickness={5} sx={{ color: accent, flexShrink: 0 }} />
            }
            <Typography
              sx={{
                flex: 1,
                fontSize: 13.5,
                fontWeight: 700,
                color: '#ffffff',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: 1.3,
              }}
            >
              {personName}
            </Typography>
            <IconButton
              size="small"
              onClick={onClose}
              aria-label="fechar"
              sx={{ color: 'rgba(255,255,255,0.35)', p: 0.25, ml: 0.25, '&:hover': { color: 'rgba(255,255,255,0.7)' } }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>

          {/* Row 2 — % + status message + view button */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pl: 3.25 }}>
            {!success && progress !== undefined && (
              <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: accent, flexShrink: 0, lineHeight: 1 }}>
                {Math.round(pct)}%
              </Typography>
            )}
            <Typography
              sx={{
                flex: 1,
                fontSize: 12,
                color: success ? accent : 'rgba(255,255,255,0.6)',
                fontWeight: success ? 600 : 400,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                lineHeight: 1.4,
                transition: 'color 0.3s ease',
              }}
            >
              {secondaryLine}
            </Typography>
            <Tooltip title={success ? 'Ver resultado' : 'Ver progresso'} placement="left">
              <IconButton
                size="small"
                onClick={onView}
                sx={{ color: 'rgba(255,255,255,0.35)', p: 0.25, flexShrink: 0, '&:hover': { color: accent } }}
              >
                <VisibilityIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Bottom bar: progress fill OR success countdown */}
        {(progress !== undefined || showCountdown) && (
          <>
            <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 3, backgroundColor: alpha(accent, 0.15) }} />
            {showCountdown ? (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  height: 3,
                  backgroundColor: accent,
                  animation: `${countdown} ${successLingerMs}ms linear forwards`,
                  animationPlayState: hovered ? 'paused' : 'running',
                }}
                onAnimationEnd={onClose}
              />
            ) : (
              <Box sx={{
                position: 'absolute', bottom: 0, left: 0, height: 3,
                width: `${pct}%`, backgroundColor: accent,
                transition: 'width 400ms ease, background-color 0.3s ease',
                borderRadius: '0 2px 2px 0',
              }} />
            )}
          </>
        )}
      </Paper>
    </Snackbar>
  );
};

export default SyncActiveToast;
